import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, ChevronRight, ChevronDown, Users, Target, Award, Calendar, MapPin, CheckCircle2, ExternalLink, MessageCircle } from 'lucide-react';
import FullscreenModal from './FullscreenModal';
import ColabModal from './ColabModal';
import AnimatedCounter from './AnimatedCounter';
import ScrollReveal, { StaggerItem } from './ScrollReveal';
import { useI18n } from '../lib/i18n';

// i18n helper — resolves { pt, en } objects or returns plain strings
const L = (val, lang) => {
  if (val == null) return '';
  if (typeof val === 'object' && !Array.isArray(val)) return val[lang] || val.pt || '';
  return val;
};
const La = (arr, lang) => Array.isArray(arr) ? arr.map((v) => L(v, lang)) : [];

// ══════════════════════════════════════════════════════════════
// PROJECT DATA — cada projeto é uma landing page completa
// ══════════════════════════════════════════════════════════════

const projects = [
  {
    id: 'a360',
    title: 'A360',
    tag: { pt: 'Qualificação Global', en: 'Global Qualification' },
    img: 'https://images.unsplash.com/photo-1637441210953-5d99e6d6c37e?w=1200&q=80',
    intro: {
      pt: 'Formação técnica audiovisual de excelência, totalmente gratuita.',
      en: 'Excellence in audiovisual technical training — completely free of charge.',
    },
    heroLine: {
      pt: 'O programa que já transformou milhares de vidas no audiovisual brasileiro.',
      en: 'The program that has already transformed thousands of lives in Brazilian audiovisual.',
    },
    context: {
      pt: 'O Brasil é o 10º maior mercado audiovisual do mundo, mas tem um dos menores índices de formação técnica da América Latina. O A360 preenche esse vazio: 792 horas de capacitação gratuita, com patrocínio das maiores plataformas do mundo.',
      en: 'Brazil is the 10th largest audiovisual market in the world, yet has one of the lowest rates of technical training in Latin America. A360 fills that gap: 792 hours of free professional education, sponsored by the world\'s largest streaming platforms.',
    },
    deliverables: [
      { pt: '14 trilhas de formação — do roteiro à pós-produção', en: '14 training tracks — from screenwriting to post-production' },
      { pt: '4 níveis de profundidade (introdutório ao avançado)', en: '4 levels of depth (introductory to advanced)' },
      { pt: 'Sets de filmagem reais como ambiente de aprendizado', en: 'Real film sets as the learning environment' },
      { pt: 'Auxílio financeiro para dedicação exclusiva', en: 'Financial assistance for full-time dedication' },
      { pt: 'Certificação reconhecida pelo mercado', en: 'Industry-recognized certification' },
      { pt: 'Pipeline de empregabilidade com parceiros da indústria', en: 'Employability pipeline with industry partners' },
    ],
    partners: ['Netflix', 'Amazon MGM Studios', 'Globoplay', 'STRIMA'],
    differentials: [
      { icon: Award, text: { pt: 'Patrocínio das 3 maiores plataformas de streaming do Brasil', en: 'Sponsored by the 3 largest streaming platforms in Brazil' } },
      { icon: Users, text: { pt: 'Fundamentado em diversidade, equidade e inclusão', en: 'Grounded in diversity, equity, and inclusion' } },
      { icon: Target, text: { pt: '94% de empregabilidade entre os formados', en: '94% employability rate among graduates' } },
      { icon: Calendar, text: { pt: 'Programa contínuo — novas turmas a cada semestre', en: 'Ongoing program — new cohorts every semester' } },
    ],
    stats: [
      { end: 792, suffix: 'h', label: { pt: 'De formação gratuita', en: 'Of free training' } },
      { end: 3200, suffix: '+', label: { pt: 'Profissionais formados', en: 'Trained professionals' } },
      { end: 94, suffix: '%', label: { pt: 'Taxa de empregabilidade', en: 'Employability rate' } },
      { end: 14, suffix: '', label: { pt: 'Trilhas de formação', en: 'Training tracks' } },
    ],
    audience: [
      { pt: 'Jovens de 18 a 35 anos', en: 'Young people aged 18 to 35' },
      { pt: 'Profissionais em transição de carreira', en: 'Professionals transitioning careers' },
      { pt: 'Comunidades sub-representadas no audiovisual', en: 'Underrepresented communities in audiovisual' },
      { pt: 'Qualquer pessoa com interesse real na indústria', en: 'Anyone with a genuine interest in the industry' },
    ],
    faq: [
      {
        q: { pt: 'Quanto custa?', en: 'How much does it cost?' },
        a: { pt: 'Zero. O programa é 100% gratuito, com auxílio financeiro para alunos selecionados.', en: 'Zero. The program is 100% free, with financial assistance for selected students.' },
      },
      {
        q: { pt: 'Preciso de experiência prévia?', en: 'Do I need prior experience?' },
        a: { pt: 'Não para o nível introdutório. Níveis avançados exigem portfólio ou formação anterior.', en: 'Not for the introductory level. Advanced levels require a portfolio or prior training.' },
      },
      {
        q: { pt: 'Onde acontece?', en: 'Where does it take place?' },
        a: { pt: 'Presencial em São Paulo, com módulos híbridos para algumas trilhas.', en: 'In-person in São Paulo, with hybrid modules for some tracks.' },
      },
    ],
    ctaText: { pt: 'Quero me inscrever no A360', en: 'I want to enroll in A360' },
    colabText: { pt: 'Quero ser parceiro do A360', en: 'I want to be an A360 partner' },
  },
  {
    id: 'fabrica',
    title: 'Fábrica de Conteúdos',
    tag: { pt: 'Estúdio-Escola', en: 'Studio-School' },
    img: 'https://uuvsbgxrndwcnudctjlw.supabase.co/storage/v1/object/public/site-images/img_0796-hdr.webp',
    intro: {
      pt: 'O lugar onde a lógica da ciência encontra a linguagem do entretenimento.',
      en: 'Where the logic of science meets the language of entertainment.',
    },
    heroLine: {
      pt: '5 anos de produção ininterrupta. Pipeline industrial. Qualidade de cinema.',
      en: '5 years of uninterrupted production. Industrial pipeline. Cinema-grade quality.',
    },
    context: {
      pt: 'Produzir conteúdo de qualidade exige pipeline, não improviso. A Fábrica de Conteúdos opera com a mesma lógica de um estúdio de cinema: cada etapa mapeada, cada recurso otimizado, cada entrega mensurável. O conceito de estúdio-escola faz o diferencial: quem aprende produz, quem produz ensina, quem ensina pesquisa.',
      en: 'Quality content production demands a pipeline, not improvisation. The Fábrica de Conteúdos operates with the same logic as a film studio: every stage mapped, every resource optimized, every deliverable measurable. The studio-school concept makes the difference: those who learn produce, those who produce teach, those who teach research.',
    },
    deliverables: [
      { pt: 'Documentários com rigor científico e narrativa cinematográfica', en: 'Documentaries with scientific rigor and cinematic storytelling' },
      { pt: 'Séries educacionais e institucionais em 4K', en: 'Educational and institutional series in 4K' },
      { pt: 'Podcasts com produção profissional', en: 'Professionally produced podcasts' },
      { pt: 'Cursos completos para graduação, pós e mercado', en: 'Full courses for undergraduate, graduate, and corporate audiences' },
      { pt: 'Pipeline de IA integrado aos processos autorais', en: 'AI pipeline integrated into the creative process' },
      { pt: 'Coprodução com estúdio-escola totalmente equipado', en: 'Co-production with a fully equipped studio-school' },
    ],
    partners: ['Secretarias estaduais', 'Universidades parceiras', 'Plataformas EAD', 'Produtoras independentes'],
    differentials: [
      { icon: Award, text: { pt: '10.000+ horas de conteúdo produzidas em 19 anos', en: '10,000+ hours of content produced over 19 years' } },
      { icon: Target, text: { pt: 'Pipeline industrial — previsibilidade de prazo e custo', en: 'Industrial pipeline — predictable timelines and costs' } },
      { icon: Users, text: { pt: 'Modelo estúdio-escola: produção integrada à formação', en: 'Studio-school model: production integrated with training' } },
      { icon: MapPin, text: { pt: '1.500m² de estúdio profissional equipado em SP', en: '1,500m² of fully equipped professional studio in SP' } },
    ],
    stats: [
      { end: 10000, suffix: '+', label: { pt: 'Horas de conteúdo', en: 'Hours of content' } },
      { end: 19, suffix: '', label: { pt: 'Anos de operação', en: 'Years of operation' } },
      { end: 200, suffix: '+', label: { pt: 'Produções entregues', en: 'Productions delivered' } },
      { end: 1500, suffix: 'm²', label: { pt: 'De estúdio equipado', en: 'Of equipped studio' } },
    ],
    audience: [
      { pt: 'Empresas que precisam de conteúdo institucional', en: 'Companies that need institutional content' },
      { pt: 'Órgãos públicos com projetos de comunicação', en: 'Public agencies with communication projects' },
      { pt: 'Plataformas de streaming e distribuição', en: 'Streaming and distribution platforms' },
      { pt: 'Produtoras buscando coprodução', en: 'Production companies seeking co-production' },
    ],
    faq: [
      {
        q: { pt: 'Posso usar o estúdio sem contratar produção?', en: 'Can I use the studio without hiring the production team?' },
        a: { pt: 'Sim. Oferecemos locação do espaço com ou sem equipe técnica.', en: 'Yes. We offer space rental with or without a technical crew.' },
      },
      {
        q: { pt: 'Qual o prazo médio?', en: 'What is the average turnaround time?' },
        a: { pt: 'Vídeos institucionais: 4-6 semanas. Documentários: 3-6 meses. Séries: sob consulta.', en: 'Institutional videos: 4–6 weeks. Documentaries: 3–6 months. Series: upon consultation.' },
      },
      {
        q: { pt: 'Vocês fazem EAD/e-learning?', en: 'Do you produce e-learning content?' },
        a: { pt: 'Sim. É uma das nossas especialidades — milhares de horas produzidas para universidades e corporações.', en: 'Yes. It is one of our specialties — thousands of hours produced for universities and corporations.' },
      },
    ],
    ctaText: { pt: 'Quero produzir com a Fábrica', en: 'I want to produce with Fábrica' },
    colabText: { pt: 'Quero ser parceiro', en: 'I want to be a partner' },
  },
  {
    id: 'hub-sampa-games',
    title: 'Hub Sampa Games',
    tag: { pt: 'Ecossistema de Games', en: 'Games Ecosystem' },
    img: 'https://uuvsbgxrndwcnudctjlw.supabase.co/storage/v1/object/public/site-images/h.sampagames-37-(1).webp',
    intro: {
      pt: 'Polo de criação onde games nascem como linguagem, narrativa e indústria.',
      en: 'A creative hub where games are born as language, narrative, and industry.',
    },
    heroLine: {
      pt: 'O maior hub de games da cidade de São Paulo. Gestão IBRC.',
      en: 'The largest games hub in the city of São Paulo. Managed by IBRC.',
    },
    context: {
      pt: 'Games são a maior indústria criativa do planeta — maior que cinema e música somados. O Hub Sampa Games, criado pela Prefeitura de São Paulo via ADE SAMPA e gerido educacionalmente pelo IBRC, forma desenvolvedores completos na interseção entre tecnologia, arte e negócios.',
      en: 'Games are the largest creative industry on the planet — bigger than film and music combined. The Hub Sampa Games, created by the City of São Paulo through ADE SAMPA and educationally managed by IBRC, trains complete developers at the intersection of technology, art, and business.',
    },
    deliverables: [
      { pt: 'Formação em programação e game engines (Unity, Unreal)', en: 'Training in programming and game engines (Unity, Unreal)' },
      { pt: 'Game design, level design e narrativa interativa', en: 'Game design, level design, and interactive narrative' },
      { pt: 'Arte digital, concept art e animação', en: 'Digital art, concept art, and animation' },
      { pt: 'Gestão de estúdio e pitch para publishers', en: 'Studio management and pitching to publishers' },
      { pt: 'Laboratório com hardware profissional', en: 'Laboratory with professional hardware' },
      { pt: 'Publicação e distribuição multiplataforma', en: 'Multiplatform publishing and distribution' },
    ],
    partners: ['Prefeitura de São Paulo', 'ADE SAMPA', 'ABRAGAMES', 'Empresas do ecossistema'],
    differentials: [
      { icon: MapPin, text: { pt: 'Hub físico com laboratório profissional em SP', en: 'Physical hub with professional laboratory in SP' } },
      { icon: Users, text: { pt: '100+ desenvolvedores formados e atuando no mercado', en: '100+ trained developers actively working in the industry' } },
      { icon: Target, text: { pt: 'Jogos reais publicados por alunos durante o programa', en: 'Real games published by students during the program' } },
      { icon: Award, text: { pt: 'Conexão com ecossistema nacional e internacional de games', en: 'Connected to the national and international games ecosystem' } },
    ],
    stats: [
      { end: 100, suffix: '+', label: { pt: 'Desenvolvedores formados', en: 'Trained developers' } },
      { end: 5, suffix: '', label: { pt: 'Áreas de formação', en: 'Training areas' } },
      { end: 20, suffix: '+', label: { pt: 'Jogos publicados', en: 'Published games' } },
    ],
    audience: [
      { pt: 'Jovens interessados em desenvolvimento de games', en: 'Young people interested in game development' },
      { pt: 'Artistas digitais e programadores', en: 'Digital artists and programmers' },
      { pt: 'Empreendedores do setor', en: 'Entrepreneurs in the sector' },
      { pt: 'Empresas buscando talentos em games', en: 'Companies looking for games talent' },
    ],
    faq: [
      {
        q: { pt: 'Preciso saber programar?', en: 'Do I need to know how to code?' },
        a: { pt: 'Não para todas as trilhas. Game design, arte e narrativa não exigem código.', en: 'Not for all tracks. Game design, art, and narrative do not require coding.' },
      },
      {
        q: { pt: 'Os jogos são publicados de verdade?', en: 'Are the games actually published?' },
        a: { pt: 'Sim. Projetos finais são publicados em plataformas reais como Steam e itch.io.', en: 'Yes. Final projects are published on real platforms such as Steam and itch.io.' },
      },
    ],
    ctaText: { pt: 'Quero participar do Hub', en: 'I want to join the Hub' },
    colabText: { pt: 'Quero apoiar o Hub', en: 'I want to support the Hub' },
  },
  {
    id: 'amplificacine',
    title: 'AmplificaCine',
    tag: { pt: 'Aceleração Audiovisual', en: 'Audiovisual Acceleration' },
    img: 'https://uuvsbgxrndwcnudctjlw.supabase.co/storage/v1/object/public/site-images/amplifica_kickoff-2026_-00047.webp',
    intro: {
      pt: 'Aceleração para micro e pequenas empresas do audiovisual paulistano.',
      en: 'Acceleration program for micro and small audiovisual companies in São Paulo.',
    },
    heroLine: {
      pt: 'R$50 mil por empresa. 3 anos de resultados comprovados.',
      en: 'R$50K per company. 3 years of proven results.',
    },
    context: {
      pt: 'O audiovisual paulistano tem talento de sobra e gestão de menos. O AmplificaCine injeta capital, capacitação em gestão e testes de mercado reais para transformar talento em negócio sustentável. O IBRC lidera a gestão há 3 anos consecutivos.',
      en: 'The São Paulo audiovisual scene has plenty of talent but lacks management skills. AmplificaCine injects capital, management training, and real market testing to transform talent into sustainable business. IBRC has led the program for 3 consecutive years.',
    },
    deliverables: [
      { pt: 'R$50 mil de aporte financeiro por empresa acelerada', en: 'R$50K financial investment per accelerated company' },
      { pt: 'Capacitação em gestão, finanças e modelagem de negócio', en: 'Training in management, finance, and business modeling' },
      { pt: 'Mentoria com especialistas do mercado', en: 'Mentorship from industry experts' },
      { pt: 'Testes de mercado reais com feedback estruturado', en: 'Real market tests with structured feedback' },
      { pt: 'Pitch day para investidores e distribuidores', en: 'Pitch day for investors and distributors' },
      { pt: 'Rede de contatos permanente pós-aceleração', en: 'Permanent professional network after acceleration' },
    ],
    partners: ['ADE SAMPA', 'SPCINE', 'Prefeitura de São Paulo', 'Investidores do setor'],
    differentials: [
      { icon: Award, text: { pt: 'R$50 mil de aporte direto — não é empréstimo', en: 'R$50K direct investment — not a loan' } },
      { icon: Target, text: { pt: 'Testes de mercado reais, não simulados', en: 'Real market tests, not simulations' } },
      { icon: Users, text: { pt: 'Mentores que são empresários do audiovisual', en: 'Mentors who are audiovisual business owners' } },
      { icon: Calendar, text: { pt: '3 anos consecutivos de gestão IBRC', en: '3 consecutive years of IBRC management' } },
    ],
    stats: [
      { end: 50, suffix: 'k', prefix: 'R$', label: { pt: 'Aporte por empresa', en: 'Investment per company' } },
      { end: 3, suffix: '', label: { pt: 'Anos de gestão IBRC', en: 'Years of IBRC management' } },
      { end: 30, suffix: '+', label: { pt: 'Empresas aceleradas', en: 'Accelerated companies' } },
    ],
    audience: [
      { pt: 'Micro e pequenas empresas do audiovisual', en: 'Micro and small audiovisual companies' },
      { pt: 'Produtoras independentes de São Paulo', en: 'Independent production companies from São Paulo' },
      { pt: 'Empreendedores criativos com CNPJ ativo', en: 'Creative entrepreneurs with an active business registration' },
    ],
    faq: [
      {
        q: { pt: 'Preciso devolver o aporte?', en: 'Do I need to repay the investment?' },
        a: { pt: 'Não. É subvenção econômica, não empréstimo.', en: 'No. It is an economic grant, not a loan.' },
      },
      {
        q: { pt: 'Minha empresa precisa ser de SP?', en: 'Does my company need to be based in SP?' },
        a: { pt: 'Sim. O programa é para empresas do município de São Paulo.', en: 'Yes. The program is for companies registered in the city of São Paulo.' },
      },
    ],
    ctaText: { pt: 'Quero acelerar minha empresa', en: 'I want to accelerate my company' },
    colabText: { pt: 'Quero patrocinar', en: 'I want to sponsor' },
  },
  {
    id: 'sororidade-conecta',
    title: 'Sororidade Conecta',
    tag: { pt: 'Empreendedorismo Feminino', en: 'Female Entrepreneurship' },
    img: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=1200&q=80',
    intro: {
      pt: 'Ecossistema phygital para fortalecer o empreendedorismo feminino.',
      en: 'A phygital ecosystem to strengthen female entrepreneurship.',
    },
    heroLine: {
      pt: '500+ conteúdos. Mentorias reais. Plataforma digital própria.',
      en: '500+ pieces of content. Real mentorships. Proprietary digital platform.',
    },
    context: {
      pt: 'Empreendedoras brasileiras enfrentam barreiras estruturais: acesso a crédito, rede de contatos e capacitação técnica. O Sororidade Conecta integra uma plataforma digital com agenda presencial, mentorias e conteúdo exclusivo, tudo alinhado à agenda ESG.',
      en: 'Brazilian women entrepreneurs face structural barriers: access to credit, professional networks, and technical training. Sororidade Conecta integrates a digital platform with an in-person agenda, mentorships, and exclusive content — all aligned with the ESG agenda.',
    },
    deliverables: [
      { pt: '500+ conteúdos exclusivos sobre empreendedorismo', en: '500+ exclusive pieces of entrepreneurship content' },
      { pt: 'Plataforma digital própria com acesso gratuito', en: 'Proprietary digital platform with free access' },
      { pt: 'Mentorias individuais e em grupo', en: 'Individual and group mentorships' },
      { pt: 'Networking estruturado entre empreendedoras', en: 'Structured networking among women entrepreneurs' },
      { pt: 'Capacitação técnica e criativa', en: 'Technical and creative skill-building' },
      { pt: 'Integração com agenda ESG corporativa', en: 'Integration with corporate ESG agenda' },
    ],
    partners: ['Mulher Líder Niterói', 'Parceiros ESG', 'Plataformas educacionais'],
    differentials: [
      { icon: Users, text: { pt: '250 acessos gratuitos para o Mulher Líder em Niterói', en: '250 free access spots for Mulher Líder in Niterói' } },
      { icon: Target, text: { pt: 'Modelo phygital — digital + presencial integrados', en: 'Phygital model — digital and in-person integrated' } },
      { icon: Award, text: { pt: 'Alinhamento total com agenda ESG corporativa', en: 'Full alignment with the corporate ESG agenda' } },
    ],
    stats: [
      { end: 500, suffix: '+', label: { pt: 'Conteúdos exclusivos', en: 'Exclusive content pieces' } },
      { end: 250, suffix: '', label: { pt: 'Acessos gratuitos', en: 'Free access spots' } },
    ],
    audience: [
      { pt: 'Empreendedoras em qualquer estágio', en: 'Women entrepreneurs at any stage' },
      { pt: 'Empresas com agenda ESG', en: 'Companies with an ESG agenda' },
      { pt: 'Organizações de apoio ao empreendedorismo feminino', en: 'Organizations that support female entrepreneurship' },
    ],
    faq: [
      {
        q: { pt: 'É gratuito?', en: 'Is it free?' },
        a: { pt: 'Sim para as vagas subsidiadas. A plataforma também oferece acesso premium com conteúdos adicionais.', en: 'Yes for subsidized spots. The platform also offers premium access with additional content.' },
      },
    ],
    ctaText: { pt: 'Quero participar', en: 'I want to participate' },
    colabText: { pt: 'Quero apoiar', en: 'I want to support' },
  },
  {
    id: 'publicus',
    title: 'PUBLICUS',
    tag: { pt: 'Escola de IA', en: 'AI School' },
    img: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&q=80',
    intro: {
      pt: 'IA é bem público. Direito de todo cidadão, letramento que gera soberania.',
      en: 'AI is a public good. Every citizen\'s right — literacy that generates sovereignty.',
    },
    heroLine: {
      pt: 'O Brasil é o último da América Latina em letramento digital. A PUBLICUS existe para mudar isso.',
      en: 'Brazil ranks last in Latin America in digital literacy. PUBLICUS exists to change that.',
    },
    context: {
      pt: '62% dos professores brasileiros nunca receberam formação em TIC. O governo federal destinou R$23 bilhões para IA em educação. A PUBLICUS forma cidadãos com domínio real sobre a tecnologia que governa suas vidas — de professores a gestores, de empreendedores a estudantes.',
      en: '62% of Brazilian teachers have never received ICT training. The federal government allocated R$23 billion for AI in education. PUBLICUS develops citizens with genuine command over the technology that governs their lives — from teachers to managers, from entrepreneurs to students.',
    },
    deliverables: [
      { pt: 'Letramento em IA — o que é, como funciona, como avaliar criticamente', en: 'AI literacy — what it is, how it works, how to assess it critically' },
      { pt: 'Agent Coding — construir agentes de IA com ferramentas reais', en: 'Agent Coding — building AI agents with real tools' },
      { pt: 'Governança Agêntica — frameworks para organizações', en: 'Agentic Governance — frameworks for organizations' },
      { pt: 'Teoria Crítica — ética, viés, impacto social da IA', en: 'Critical Theory — ethics, bias, and social impact of AI' },
      { pt: 'Especialização Setorial — IA aplicada por domínio', en: 'Sector Specialization — AI applied by domain' },
    ],
    partners: ['Parceiros técnicos em IA', 'Secretarias de educação', 'Universidades', 'Organizações internacionais'],
    differentials: [
      { icon: Target, text: { pt: '5 domínios integrados — do letramento à especialização', en: '5 integrated domains — from literacy to specialization' } },
      { icon: Users, text: { pt: 'Para professores, gestores, empreendedores, estudantes e cidadãos', en: 'For teachers, managers, entrepreneurs, students, and citizens' } },
      { icon: Award, text: { pt: 'Projetado para impactar indicadores PISA 2029', en: 'Designed to impact PISA 2029 indicators' } },
      { icon: Calendar, text: { pt: 'Mercado global de IA educacional: USD 1,9B até 2035', en: 'Global educational AI market: USD 1.9B by 2035' } },
    ],
    stats: [
      { end: 5, suffix: '', label: { pt: 'Domínios integrados', en: 'Integrated domains' } },
      { end: 62, suffix: '%', label: { pt: 'Professores sem formação TIC', en: 'Teachers without ICT training' } },
      { end: 23, suffix: 'B', prefix: 'R$', label: { pt: 'Investimento governo federal', en: 'Federal government investment' } },
    ],
    audience: [
      { pt: 'Professores e educadores', en: 'Teachers and educators' },
      { pt: 'Gestores públicos e corporativos', en: 'Public and corporate managers' },
      { pt: 'Empreendedores que precisam de IA', en: 'Entrepreneurs who need AI' },
      { pt: 'Estudantes universitários', en: 'University students' },
      { pt: 'Cidadãos curiosos', en: 'Curious citizens' },
    ],
    faq: [
      {
        q: { pt: 'Preciso saber programar?', en: 'Do I need to know how to code?' },
        a: { pt: 'Não para os módulos de letramento e teoria crítica. Agent Coding exige noções básicas.', en: 'Not for the literacy and critical theory modules. Agent Coding requires basic knowledge.' },
      },
      {
        q: { pt: 'Qual a diferença para cursos de IA tradicionais?', en: 'What makes this different from traditional AI courses?' },
        a: { pt: 'PUBLICUS é cidadania digital, não engenharia. Formamos quem usa, avalia e governa IA — não só quem programa.', en: 'PUBLICUS is about digital citizenship, not engineering. We train people who use, evaluate, and govern AI — not just those who program it.' },
      },
    ],
    ctaText: { pt: 'Quero aprender IA', en: 'I want to learn AI' },
    colabText: { pt: 'Quero levar para minha organização', en: 'I want to bring this to my organization' },
  },
  {
    id: 'cria',
    title: 'CRIA',
    tag: { pt: 'Pesquisa & Inovação', en: 'Research & Innovation' },
    img: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=1200&q=80',
    intro: {
      pt: 'ICT credenciada. Pesquisa que vira produto.',
      en: 'Accredited ICT. Research that becomes product.',
    },
    heroLine: {
      pt: 'Acesso direto a FINEP, EMBRAPII e R$6 bilhões do FNDCT.',
      en: 'Direct access to FINEP, EMBRAPII, and R$6 billion from FNDCT.',
    },
    context: {
      pt: 'O CRIA é o braço de pesquisa e inovação do IBRC, credenciado como ICT nos termos do Marco Legal de CT&I. Isso significa acesso direto a mecanismos de fomento como FINEP, EMBRAPII e os 15 fundos setoriais do FNDCT. Pesquisa aqui não é acadêmica — vira produto, patente, tecnologia transferida.',
      en: 'CRIA is the research and innovation arm of IBRC, accredited as an ICT under the Brazilian Legal Framework for Science, Technology & Innovation. This means direct access to funding mechanisms such as FINEP, EMBRAPII, and the 15 sectoral funds of FNDCT. Research here is not academic — it becomes product, patent, transferred technology.',
    },
    deliverables: [
      { pt: 'NIT próprio — gestão de PI, patentes, licenciamento', en: 'Proprietary NIT — IP management, patents, licensing' },
      { pt: 'Transferência de tecnologia para empresas parceiras', en: 'Technology transfer to partner companies' },
      { pt: 'IA educacional — plataformas adaptativas e agentes', en: 'Educational AI — adaptive platforms and agents' },
      { pt: 'Produção audiovisual — pipelines e automação', en: 'Audiovisual production — pipelines and automation' },
      { pt: 'Games — engines educacionais e serious games', en: 'Games — educational engines and serious games' },
      { pt: 'Inteligência de mercado — modelos preditivos', en: 'Market intelligence — predictive models' },
    ],
    partners: ['FINEP', 'EMBRAPII', 'FNDCT', 'Marco Legal de CT&I', 'Empresas parceiras'],
    differentials: [
      { icon: Award, text: { pt: 'ICT credenciada — acesso a fomento federal direto', en: 'Accredited ICT — direct access to federal funding' } },
      { icon: Target, text: { pt: 'NIT próprio — gestão profissional de propriedade intelectual', en: 'Proprietary NIT — professional intellectual property management' } },
      { icon: Users, text: { pt: 'Modelo EMBRAPII empresa-ICT para projetos conjuntos', en: 'EMBRAPII company-ICT model for joint projects' } },
      { icon: MapPin, text: { pt: '4 linhas de pesquisa com aplicação real', en: '4 research lines with real-world application' } },
    ],
    stats: [
      { end: 4, suffix: '', label: { pt: 'Linhas de pesquisa', en: 'Research lines' } },
      { end: 15, suffix: '', label: { pt: 'Fundos setoriais FNDCT', en: 'FNDCT sectoral funds' } },
      { end: 6, suffix: 'B', prefix: 'R$', label: { pt: 'Disponíveis via FNDCT', en: 'Available through FNDCT' } },
    ],
    audience: [
      { pt: 'Empresas que precisam de P&D com incentivo fiscal', en: 'Companies that need R&D with tax incentives' },
      { pt: 'Organizações buscando parceria ICT', en: 'Organizations seeking ICT partnerships' },
      { pt: 'Pesquisadores e inventores', en: 'Researchers and inventors' },
      { pt: 'Startups de deep tech', en: 'Deep tech startups' },
    ],
    faq: [
      {
        q: { pt: 'O que é ICT credenciada?', en: 'What is an accredited ICT?' },
        a: { pt: 'Instituição Científica, Tecnológica e de Inovação reconhecida pelo Marco Legal de CT&I. Dá acesso a fomento e incentivos fiscais.', en: 'A Scientific, Technological, and Innovation Institution recognized by the Brazilian Legal Framework for CT&I. It grants access to public funding and tax incentives.' },
      },
      {
        q: { pt: 'Posso usar o CRIA para obter fomento EMBRAPII?', en: 'Can I use CRIA to obtain EMBRAPII funding?' },
        a: { pt: 'Sim. O IBRC é elegível como ICT parceira em projetos EMBRAPII empresa-ICT.', en: 'Yes. IBRC is eligible as a partner ICT in EMBRAPII company-ICT projects.' },
      },
    ],
    ctaText: { pt: 'Quero pesquisar com o CRIA', en: 'I want to research with CRIA' },
    colabText: { pt: 'Quero ser empresa parceira', en: 'I want to be a partner company' },
  },
];

// ══════════════════════════════════════════════════════════════
// PROJECT DETAIL — página rica por projeto
// ══════════════════════════════════════════════════════════════

function ProjectDetail({ project, onBack, onColab }) {
  const [openFaq, setOpenFaq] = useState(null);
  const { lang } = useI18n();
  const pt = lang === 'pt';

  return (
    <motion.div
      key={project.id}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {/* ── HERO ── */}
      <div className="relative h-[35vh] md:h-[50vh] overflow-hidden">
        <img src={project.img} alt={project.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
          <span className="font-oswald text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FFC107]">
            {L(project.tag, lang)}
          </span>
          <h2 className="font-oswald text-3xl md:text-5xl uppercase text-white mt-2 leading-[0.95]">
            {project.title}
          </h2>
          <p className="font-manrope text-lg text-white/70 mt-3 max-w-[600px]">
            {L(project.heroLine, lang)}
          </p>
        </div>
      </div>

      {/* ── STATS BAR ── */}
      <div className="bg-[#FFC107] px-6 md:px-10">
        <div className="max-w-[1000px] mx-auto py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {project.stats.map((stat, i) => (
            <div key={i} className="text-center">
              <span className="font-oswald text-2xl md:text-3xl text-black font-bold">
                {stat.prefix || ''}<AnimatedCounter end={stat.end} suffix={stat.suffix || ''} />
              </span>
              <p className="font-manrope text-[10px] text-black/50 uppercase tracking-wider mt-1">{L(stat.label, lang)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── PARCEIROS ── */}
      {project.partners && (
        <div className="px-6 md:px-10 py-6 border-b border-black/5">
          <div className="max-w-[1000px] mx-auto flex flex-wrap items-center gap-6">
            <span className="font-oswald text-[10px] uppercase tracking-[0.2em] text-black/25">{pt ? 'Parceiros' : 'Partners'}</span>
            {project.partners.map((p, i) => (
              <span key={i} className="font-manrope text-sm text-black/40 font-medium">{p}</span>
            ))}
          </div>
        </div>
      )}

      {/* ── CONTEXTO ── */}
      <section className="py-12 md:py-16 px-6 md:px-10">
        <div className="max-w-[800px]">
          <ScrollReveal preset="fade-up">
            <div className="section-divider mb-4" />
            <p className="font-serif italic text-lg text-black/50 mb-6">{L(project.intro, lang)}</p>
            <p className="font-manrope text-black/60 text-base md:text-lg leading-relaxed">{L(project.context, lang)}</p>
          </ScrollReveal>
        </div>
      </section>

      {/* ── DIFERENCIAIS ── */}
      <section className="py-12 md:py-16 px-6 md:px-10 bg-black text-white">
        <div className="max-w-[1000px] mx-auto">
          <ScrollReveal preset="fade-up">
            <div className="section-divider mb-4" />
            <h3 className="font-oswald text-xl md:text-2xl uppercase mb-8">{pt ? 'Por que' : 'Why'} {project.title}</h3>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {project.differentials.map((d, i) => (
              <ScrollReveal key={i} preset="fade-up" delay={i * 0.08}>
                <div className="flex items-start gap-4 p-5 border border-white/5 hover:border-[#FFC107]/20 transition-colors">
                  <d.icon size={22} className="text-[#FFC107] mt-0.5 flex-shrink-0" />
                  <p className="font-manrope text-white/60 text-sm leading-relaxed">{L(d.text, lang)}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── ENTREGAS ── */}
      <section className="py-12 md:py-16 px-6 md:px-10">
        <div className="max-w-[1000px] mx-auto">
          <ScrollReveal preset="fade-up">
            <div className="section-divider mb-4" />
            <h3 className="font-oswald text-xl md:text-2xl uppercase mb-8">{pt ? 'O que entregamos' : 'What we deliver'}</h3>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {project.deliverables.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                viewport={{ once: true }}
                className="flex items-start gap-3 p-4 bg-black/[0.02] border border-black/5 hover:border-[#FFC107]/30 transition-colors"
              >
                <CheckCircle2 size={18} className="text-[#FFC107] mt-0.5 flex-shrink-0" />
                <span className="font-manrope text-sm text-black/60">{L(item, lang)}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PARA QUEM ── */}
      <section className="py-12 md:py-16 px-6 md:px-10 bg-black/[0.02]">
        <div className="max-w-[800px] mx-auto">
          <ScrollReveal preset="fade-up">
            <div className="flex items-center gap-3 mb-6">
              <Users size={20} className="text-[#FFC107]" />
              <h3 className="font-oswald text-xl uppercase">{pt ? 'Para quem é' : 'Who is it for'}</h3>
            </div>
            <div className="space-y-3">
              {project.audience.map((item, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-black/5 last:border-0">
                  <ArrowRight size={14} className="text-[#FFC107] flex-shrink-0" />
                  <span className="font-manrope text-sm text-black/60">{L(item, lang)}</span>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── FAQ ── */}
      {project.faq && project.faq.length > 0 && (
        <section className="py-12 md:py-16 px-6 md:px-10">
          <div className="max-w-[700px] mx-auto">
            <ScrollReveal preset="fade-up">
              <h3 className="font-oswald text-xl uppercase mb-6">{pt ? 'Perguntas frequentes' : 'FAQ'}</h3>
              <div className="space-y-3">
                {project.faq.map((item, i) => (
                  <div key={i} className="border border-black/5">
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-black/[0.02] transition-colors"
                    >
                      <span className="font-manrope text-sm font-medium text-black/70 pr-4">{L(item.q, lang)}</span>
                      <ChevronDown
                        size={16}
                        className={`text-[#FFC107] flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                      />
                    </button>
                    <AnimatePresence>
                      {openFaq === i && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <p className="px-4 pb-4 font-manrope text-sm text-black/50 leading-relaxed">{L(item.a, lang)}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <section className="py-14 md:py-20 px-6 md:px-10 bg-black text-white">
        <div className="max-w-[700px] mx-auto text-center">
          <ScrollReveal preset="fade-up">
            <div className="section-divider mx-auto mb-6" />
            <h3 className="font-oswald text-2xl md:text-3xl uppercase mb-3">{L(project.heroLine, lang)}</h3>
            <p className="font-oswald text-xl text-[#FFC107] uppercase mb-8">{pt ? 'Faça parte.' : 'Be part of it.'}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                onClick={onColab}
                className="h-12 px-8 bg-[#FFC107] text-black uppercase tracking-widest text-xs font-bold hover:bg-white transition-all btn-spring inline-flex items-center justify-center gap-2"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <MessageCircle size={16} />
                {L(project.colabText, lang)}
              </motion.button>
              <motion.button
                onClick={() => { window.dispatchEvent(new CustomEvent('open-modal', { detail: 'contato' })); }}
                className="h-12 px-8 border border-white/20 text-white uppercase tracking-widest text-xs font-bold hover:border-[#FFC107] hover:text-[#FFC107] transition-all inline-flex items-center justify-center gap-2"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {pt ? 'Falar com o IBRC' : 'Talk to IBRC'}
                <ArrowRight size={16} />
              </motion.button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Back */}
      <section className="py-6 px-6 md:px-10 border-t border-black/5">
        <button
          onClick={onBack}
          className="flex items-center gap-2 font-oswald text-xs uppercase tracking-wider text-black/30 hover:text-[#FFC107] transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          {pt ? 'Ver todos os projetos' : 'View all programs'}
        </button>
      </section>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN HUB — grid + detalhe
// ══════════════════════════════════════════════════════════════

const ProjetosHub = ({ isOpen, onClose, initialProject }) => {
  const [activeProject, setActiveProject] = useState(null);
  const [colabOpen, setColabOpen] = useState(false);
  const { t, lang } = useI18n();
  const pt = lang === 'pt';

  useEffect(() => {
    if (isOpen) {
      setActiveProject(initialProject || null);
    }
  }, [isOpen, initialProject]);

  const project = projects.find((p) => p.id === activeProject);

  const breadcrumb = activeProject && project
    ? [
        { label: t('nav.projetos', 'Projetos'), onClick: () => setActiveProject(null) },
        { label: project.title },
      ]
    : [{ label: t('nav.projetos', 'Projetos') }];

  return (
    <>
      <FullscreenModal
        isOpen={isOpen}
        onClose={onClose}
        title="Projetos"
        hashId={activeProject ? `projeto-${activeProject}` : 'projetos'}
        breadcrumb={breadcrumb}
      >
        <AnimatePresence mode="wait">
          {!activeProject ? (
            /* === PROJECT GRID === */
            <motion.div
              key="grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {/* Hero */}
              <section className="py-14 md:py-20 px-6 md:px-10 bg-black text-white">
                <div className="max-w-[1200px] mx-auto">
                  <div className="section-divider mb-5" />
                  <h2 className="font-oswald text-3xl md:text-5xl uppercase mb-3 leading-tight">
                    {pt ? '7 frentes ativas.' : '7 active fronts.'}
                    <br />
                    <span className="text-[#FFC107]">{pt ? 'Nossa equipe movendo resultados.' : 'Our team driving results.'}</span>
                  </h2>
                  <p className="font-manrope text-white/50 text-base md:text-lg max-w-[600px] leading-relaxed mt-4">
                    {pt ? 'Cada projeto do IBRC nasce de uma demanda real e opera com métricas de impacto. Conheça o que fazemos — e os números que comprovam.' : 'Every IBRC project is born from a real demand and operates with impact metrics. See what we do — and the numbers that prove it.'}
                  </p>
                </div>
              </section>

              {/* Grid */}
              <section className="py-10 md:py-14 px-6 md:px-10 bg-white">
                <div className="max-w-[1200px] mx-auto">
                  <ScrollReveal preset="fade-up" stagger={0.06}>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {projects.map((p) => (
                        <StaggerItem key={p.id}>
                          <motion.button
                            onClick={() => setActiveProject(p.id)}
                            className="w-full text-left group relative overflow-hidden bg-black h-[300px]"
                            whileHover={{ y: -3 }}
                          >
                            <img
                              src={p.img}
                              alt={p.title}
                              className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                            <div className="absolute inset-0 flex flex-col justify-end p-6">
                              <span className="font-oswald text-[9px] uppercase tracking-[0.2em] text-[#FFC107] mb-2">{L(p.tag, lang)}</span>
                              <h3 className="font-oswald text-2xl uppercase text-white group-hover:text-[#FFC107] transition-colors leading-tight">{p.title}</h3>
                              <p className="font-manrope text-white/40 text-sm mt-2 line-clamp-2">{L(p.intro, lang)}</p>
                              {/* Mini stats */}
                              <div className="flex gap-4 mt-4">
                                {p.stats.slice(0, 2).map((s, i) => (
                                  <div key={i} className="text-center">
                                    <span className="font-oswald text-lg text-[#FFC107]">{s.prefix || ''}{s.end}{s.suffix}</span>
                                    <p className="font-manrope text-[9px] text-white/30 uppercase">{L(s.label, lang)}</p>
                                  </div>
                                ))}
                              </div>
                              <span className="inline-flex items-center gap-1 font-oswald text-[10px] uppercase tracking-wider text-white/20 group-hover:text-[#FFC107] mt-4 transition-colors">
                                {pt ? 'Explorar' : 'Explore'} <ChevronRight size={10} />
                              </span>
                            </div>
                          </motion.button>
                        </StaggerItem>
                      ))}
                    </div>
                  </ScrollReveal>
                </div>
              </section>
            </motion.div>
          ) : (
            /* === PROJECT DETAIL === */
            <ProjectDetail
              project={project}
              onBack={() => setActiveProject(null)}
              onColab={() => setColabOpen(true)}
            />
          )}
        </AnimatePresence>
      </FullscreenModal>

      <ColabModal isOpen={colabOpen} onClose={() => setColabOpen(false)} programName={project?.title} />
    </>
  );
};

export { projects as projectsList };
export default ProjetosHub;
